import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { pageTransition } from "@/lib/utils";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { useToast } from "@/hooks/useToast";
import {
  listAgents,
  listAgentRuns,
  triggerAgentRun,
  type AgentRead,
  type AgentRunRead,
} from "@/api/agents";
import {
  agentIcon,
  agentScheduleLabel,
  runStatusLabel,
  runStatusVariant,
  timeAgo,
} from "@/components/agents/agentMeta";
import { Play } from "lucide-react";

export function AgentsPage() {
  const [agents, setAgents] = useState<AgentRead[] | null>(null);
  const [runs, setRuns] = useState<AgentRunRead[]>([]);
  const [triggering, setTriggering] = useState<string | null>(null);
  const { addToast } = useToast();

  const refresh = async () => {
    try {
      const [a, r] = await Promise.all([
        listAgents(),
        listAgentRuns({ limit: 20 }),
      ]);
      setAgents(a);
      setRuns(r);
    } catch {
      addToast("error", "Failed to load agents");
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const runNow = async (name: string) => {
    setTriggering(name);
    try {
      await triggerAgentRun(name);
      addToast("success", `${name} run queued`);
      // Refresh after short delay to catch run row
      setTimeout(refresh, 1500);
    } catch {
      addToast("error", `Failed to trigger ${name}`);
    } finally {
      setTriggering(null);
    }
  };

  const activeCount = agents?.filter((a) => a.enabled).length ?? 0;

  return (
    <motion.div {...pageTransition}>
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold font-heading text-text-primary">
            Agents
          </h2>
          <p className="text-sm text-text-muted mt-1">
            Your team behind the team
          </p>
        </div>
        {agents !== null && (
          <Badge variant="success">{activeCount} active</Badge>
        )}
      </div>

      {/* Agent grid */}
      {agents === null ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-48 rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {agents.map((agent) => {
            const Icon = agentIcon(agent.name);
            const latestRun = runs.find((r) => r.agent_name === agent.name);
            return (
              <Card key={agent.name} className="flex flex-col">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-accent/10">
                      <Icon className="h-5 w-5 text-accent" aria-hidden="true" />
                    </div>
                    <Link
                      to={`/agents/${agent.name}`}
                      className="font-semibold font-heading text-text-primary hover:text-accent"
                    >
                      {agent.display_name}
                    </Link>
                  </div>
                  <span
                    aria-label={agent.enabled ? "Enabled" : "Disabled"}
                    title={agent.enabled ? "Enabled" : "Disabled"}
                    className={`h-2.5 w-2.5 rounded-full mt-2 ${
                      agent.enabled ? "bg-success" : "bg-text-muted/40"
                    }`}
                  />
                </div>
                <p className="text-sm text-text-muted mb-4 line-clamp-2">
                  {agent.description}
                </p>
                <div className="text-xs text-text-muted mb-2">
                  {agentScheduleLabel(agent.name, agent.schedule_cron)}
                </div>
                <div className="flex items-center gap-2 mb-4 text-xs">
                  <span className="text-text-muted">Last run:</span>
                  {latestRun ? (
                    <>
                      <span className="text-text-primary">
                        {timeAgo(latestRun.completed_at || latestRun.started_at)}
                      </span>
                      <Badge variant={runStatusVariant(latestRun.status)}>
                        {runStatusLabel(latestRun.status, latestRun.output_summary)}
                      </Badge>
                    </>
                  ) : (
                    <span className="text-text-muted">Never</span>
                  )}
                </div>
                <div className="mt-auto flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    isLoading={triggering === agent.name}
                    onClick={() => runNow(agent.name)}
                  >
                    <Play className="h-3.5 w-3.5" aria-hidden="true" />
                    Run now
                  </Button>
                  <Link
                    to={`/agents/${agent.name}`}
                    className="inline-flex items-center text-xs text-accent hover:underline px-2"
                  >
                    View details →
                  </Link>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Recent activity */}
      <Card>
        <h3 className="text-lg font-semibold font-heading text-text-primary mb-4">
          Recent activity
        </h3>
        {runs.length === 0 ? (
          <p className="text-sm text-text-muted py-8 text-center">
            No agent runs yet. Click "Run now" on any agent above to get started.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-text-muted text-left border-b border-border">
                  <th className="py-2 pr-4 font-medium">When</th>
                  <th className="py-2 pr-4 font-medium">Agent</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Trigger</th>
                  <th className="py-2 pr-4 font-medium">Tokens</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => {
                  const Icon = agentIcon(r.agent_name);
                  return (
                    <tr
                      key={r.id}
                      className="border-b border-border/50 last:border-0"
                    >
                      <td className="py-2.5 pr-4 text-text-muted whitespace-nowrap">
                        {timeAgo(r.started_at)}
                      </td>
                      <td className="py-2.5 pr-4">
                        <Link
                          to={`/agents/${r.agent_name}`}
                          className="inline-flex items-center gap-2 hover:text-accent"
                        >
                          <Icon className="h-4 w-4" aria-hidden="true" />
                          <span className="capitalize">{r.agent_name}</span>
                        </Link>
                      </td>
                      <td className="py-2.5 pr-4">
                        <Badge variant={runStatusVariant(r.status)}>
                          {runStatusLabel(r.status, r.output_summary)}
                        </Badge>
                      </td>
                      <td className="py-2.5 pr-4 text-text-muted capitalize">
                        {r.trigger_type}
                      </td>
                      <td className="py-2.5 pr-4 text-text-muted">
                        {r.tokens_used || 0}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </motion.div>
  );
}
